import * as Phaser from "phaser-ce"
import { ActivityKey } from "../../../../shared/models/ActivityKey"
import { CigaretteSprite } from "../../../generic-sprites/CigaretteSprite"
import { loadGenericAssets } from "../../../generic-sprites/GenericAssets"
import { Sound } from "../../../generic-sprites/lip-sync/Sound"
import { Word } from "../../../generic-sprites/lip-sync/Word"
import { ScientistSprite } from "../../../generic-sprites/ScientistSprite"
import { TitleSprite } from "../../../generic-sprites/TitleSprite"
import { AudioSync } from "../../../utils/AudioSync"
import { CameraZoom, ZoomInfo, ZoomTarget } from "../../../utils/CameraZoom"
import { ColorUtils } from "../../../utils/ColorUtils"
import { PhaserUtils } from "../../../utils/PhaserUtils"
import { Scale } from "../../../utils/Scale"
import { AddictionRepo } from "../AddictionRepo"
import { CravingsAssets, loadAssets } from "./CravingsAssets"
import { CravPos } from "./CravPos"

export class CravingsState extends Phaser.State {

    private static readonly KID_SCALE = 0.9
    private static readonly BLINK = "blink"

    private scientist: ScientistSprite
    private kid: Phaser.Sprite
    private hat: Phaser.Sprite
    private nicMeter: Phaser.Sprite
    private nicBar: Phaser.Sprite
    private cravBar: Phaser.Sprite
    private cig: CigaretteSprite
    private titleBar: TitleSprite
    private oneOffs: AudioSync
    private originalBarHeight: number

    private readonly words: Array<Word> = [
        new Word(429, 1175, [Sound.E, Sound.O, Sound.CON], "When a smoker"),
        new Word(1230, 1697, [Sound.FV, Sound.CON], "vaper,"),
        new Word(1827, 1958, [Sound.O], "or"),
        new Word(1976, 2368, [Sound.CON, Sound.E], "chewer"),
        new Word(2461, 3020, [Sound.CON, Sound.MBP, Sound.U, Sound.CON], "keeps using"),
        new Word(3076, 3561, [Sound.CON, Sound.O, Sound.MBP, Sound.O], "tobacco"),

        new Word(29307, 29922, [Sound.A, Sound.E, Sound.CON], "And even then"),
        new Word(30313, 31357, [Sound.CON, Sound.A, Sound.FV, Sound.L, Sound.E], "it takes several weeks"),
        new Word(31413, 32159, [Sound.O, Sound.E, Sound.CON, Sound.FV, Sound.CON], "to get rid of these"),
        new Word(32160, 33278, [Sound.FV, Sound.L, Sound.A, Sound.FV, Sound.CON], "physical cravings."),

        new Word(34061, 34825, [Sound.E, Sound.O, Sound.CON], "When nicotine is"),
        new Word(34826, 35515, [Sound.A, Sound.CON, Sound.A], "taken away"),
        new Word(35869, 36633, [Sound.O, Sound.CON, Sound.A, Sound.CON], "or withdrawn"),
        new Word(36801, 37081, [Sound.FV, Sound.O, Sound.CON], "from the"),
        new Word(37155, 37435, [Sound.O, Sound.CON], "body,"),
        new Word(38050, 38125, [Sound.CON], "the"),
        new Word(38237, 39132, [Sound.MBP, Sound.CON, Sound.A, Sound.O, Sound.O, Sound.A], "person may go through a"),
        new Word(39225, 39635, [Sound.MBP, Sound.O, Sound.CON], "process"),
        new Word(39672, 39915, [Sound.L, Sound.CON], "called"),
        new Word(40120, 40716, [Sound.CON, Sound.A, Sound.L], "withdrawal."),

        new Word(55407, 55631, [Sound.A, Sound.CON], "and other"),
        new Word(55632, 56358, [Sound.U, Sound.MBP, Sound.CON], "uncomfortable"),
        new Word(56359, 56936, [Sound.FV, Sound.E, Sound.L], "feelings."),

        new Word(57774, 58352, [Sound.CON, Sound.CON, Sound.O], "This is why"),
        new Word(58353, 58763, [Sound.O, Sound.A, Sound.CON], "so many"),
        new Word(58764, 59434, [Sound.MBP, Sound.L, Sound.O, Sound.A], "people who are"),
        new Word(59435, 60422, [Sound.CON, Sound.CON, Sound.O], "addicted to smoking,"),
        new Word(60776, 61186, [Sound.FV, Sound.A], "vaping"),
        new Word(61261, 62323, [Sound.O, Sound.E, Sound.CON, Sound.O, Sound.MBP, Sound.O], "or chewing tobacco"),
        new Word(62696, 62939, [Sound.CON], "wish"),
        new Word(62940, 63535, [Sound.CON, Sound.FV, Sound.CON], "they'd never st-"),
        new Word(63610, 64169, [Sound.A, Sound.E, Sound.CON], "arted in the"),
        new Word(64170, 64915, [Sound.FV, Sound.CON, Sound.MBP, Sound.A], "first place."),
    ]

    preload() {
        loadGenericAssets(this.game)
        loadAssets(this.game)
    }

    async create() {
        await PhaserUtils.sharedCreateFunctions(this.game)
        ColorUtils.setGameBackgroundColorGreen(this.game)
        this.game.world.setBounds(0, 0, Scale.width() * 1.5, Scale.height())

        this.titleBar = new TitleSprite(this.game, CravPos.titleY(), "Cravings", false)

        const chair = this.game.add.image(CravPos.chairX(), CravPos.chairY(), CravingsAssets.CHAIR)
        Scale.scaleSprite(chair, CravingsState.KID_SCALE)
        this.kid = this.game.add.sprite(CravPos.kidX(), CravPos.kidY(), CravingsAssets.KID)
        Scale.scaleSprite(this.kid, CravingsState.KID_SCALE)
        const chairArms = this.game.add.image(CravPos.chairArmsX(), CravPos.chairArmsY(),
            CravingsAssets.CHAIR_ARMS)
        Scale.scaleSprite(chairArms, CravingsState.KID_SCALE)
        this.hat = this.game.add.sprite(CravPos.hatX(), CravPos.hatY(), CravingsAssets.CRAVINGS_HAT)
        Scale.scaleSprite(this.hat, CravingsState.KID_SCALE)
        this.hat.animations.add(CravingsState.BLINK, [1, 2])

        const blueNicBar = this.game.add.image(CravPos.nicBarX(), CravPos.nicBarY(), CravingsAssets.BLUE_BAR)
        Scale.scaleSprite(blueNicBar, 1.6)
        this.nicBar = this.game.add.sprite(CravPos.nicBarX(), CravPos.nicBarY(), CravingsAssets.RED_BAR)
        Scale.scaleSprite(this.nicBar, 1.6)
        this.originalBarHeight = this.nicBar.height
        this.setBarHeight(this.nicBar, 0)
        const blueCravBar = this.game.add.sprite(CravPos.cravBarX(), CravPos.cravBarY(), CravingsAssets.BLUE_BAR)
        Scale.scaleSprite(blueCravBar, 1.6)
        this.cravBar = this.game.add.sprite(CravPos.cravBarX(), CravPos.cravBarY(), CravingsAssets.RED_BAR)
        Scale.scaleSprite(this.cravBar, 1.6)
        this.setBarHeight(this.cravBar, 0)
        this.nicMeter = this.game.add.sprite(CravPos.nicMeterX(), CravPos.nicMeterY(), CravingsAssets.NIC_METER)
        Scale.scaleSprite(this.nicMeter, CravingsState.KID_SCALE)
        this.cig = new CigaretteSprite(this.game, CravPos.cigX(), CravPos.cigY(), 0.49)
        this.cig.smoke()

        this.scientist = new ScientistSprite(this.game, CravPos.scientistX(), CravPos.scientistY(), CravingsAssets.AUDIO,
            1.15, this.words)

        this.scientist.audio.onStop.add(() => {
            AddictionRepo.instance.setCravingsAndWithdrawalAsComplete()
            const experimentOver = this.game.time.create(true)
            experimentOver.add(1500, () => {
                this.game.state.start(ActivityKey.ADDICTION_MAIN)
            })
            experimentOver.start()
        })

        this.setupTimers()
    }

    setupTimers = () => {
        let undoZoom: ZoomInfo
        this.oneOffs = new AudioSync(this.game, this.scientist.audio)

        const zoomLoc = new ZoomTarget()
        zoomLoc.x = this.nicMeter.x
        zoomLoc.y = this.nicMeter.y - Scale.height() * 0.3
        zoomLoc.width = this.nicMeter.width
        zoomLoc.height = this.nicMeter.height
        this.oneOffs.add(3000, () => {
            undoZoom = CameraZoom.zoom(this.game, zoomLoc, 1000, Scale.width() * 0.02)
            this.slideScientistOut()
        })

        this.oneOffs.add(4000, () => {
            this.hat.animations.play(CravingsState.BLINK, 10, true)
        })

        this.oneOffs.add(6000, () => {
            this.hat.animations.stop()
            this.hat.animations.frame = 0
        })

        this.slideMeter(this.nicBar, 0, 100, 9500)
        this.slideMeter(this.nicBar, 100, 65, 15500)
        this.slideMeter(this.cravBar, 0, 35, 16500)
        this.slideMeter(this.nicBar, 65, 40, 18000)
        this.slideMeter(this.cravBar, 35, 50, 19500)
        this.slideMeter(this.cravBar, 50, 80, 24000)

        let noCig: Phaser.Image
        this.oneOffs.add(28000, () => {
            noCig = this.game.add.image(CravPos.noCigX(), CravPos.noCigY(), CravingsAssets.NO_CIG)
            Scale.scaleSprite(noCig, CravingsState.KID_SCALE)
        })

        this.oneOffs.add(30000, () => {
            CameraZoom.performZoom(undoZoom)
            this.slideScientistIn()
            this.titleBar.addTitle("Withdrawal")
        })

        this.slideMeter(this.cravBar, 80, 100, 36000)
        this.slideMeter(this.nicBar, 40, 0, 36000)

        this.oneOffs.add(36000, () => {
            noCig.kill()
            this.cig.kill()
            this.kid.animations.frame = 1
        })

        this.oneOffs.add(41000, () => {
            undoZoom = CameraZoom.zoom(this.game, zoomLoc, 1000, Scale.width() * 0.02)
            this.slideScientistOut()
        })

        this.oneOffs.add(48000, () => {
            this.kid.animations.frame = 2
            const shakeRight = this.game.time.create(true)
            const shakeLeft = this.game.time.create(true)
            const shakeAmount = Scale.width() * 0.01
            shakeRight.loop(250, () => {
                this.kid.x += shakeAmount
            })
            shakeLeft.loop(250, () => {
                this.kid.x -= shakeAmount
            })
            shakeRight.start()
            shakeLeft.start(175)
        })
        this.oneOffs.add(52000, () => {
            this.kid.animations.frame = 3
        })

        this.oneOffs.add(55000, () => {
            CameraZoom.performZoom(undoZoom)
            this.slideScientistIn()
        })

        this.oneOffs.add(54000, () => {
            this.kid.animations.frame = 4
        })
        this.oneOffs.start()
    }

    private slideMeter = (bar: Phaser.Sprite, from: number, to: number, delay: number) => {
        const slide = this.game.time.create()
        let currentNumber = from
        slide.loop(20, () => {
            if (to > from) {
                currentNumber++
            } else {
                currentNumber--
            }
            this.setBarHeight(bar, currentNumber)
            if (currentNumber === to) {
                slide.destroy()
            }
        })
        this.oneOffs.add(delay, () => {
            slide.start()
        })
    }

    /**
     * Height from 0 to 100. 25 isn't really 25 because the red bars are a bit longer than the openings they show
     * through.
     */
    private setBarHeight = (bar: Phaser.Sprite, height: number) => {
        const originalBarY = bar.y + bar.height - this.originalBarHeight
        const newBarHeight = this.originalBarHeight * height / 100
        const goSouthBy = this.originalBarHeight - newBarHeight
        bar.y = originalBarY + goSouthBy
        bar.height = newBarHeight
    }

    private slideScientistOut = () => {
        this.scientist.slideTo(this.scientist.width * -0.5)
    }

    private slideScientistIn = () => {
        this.scientist.slideTo(CravPos.scientistX())
    }
}
